import React, { useState } from 'react';
import {
  Box, FormControl, IconButton, InputLabel, MenuItem, Select, SelectChangeEvent, TextField, Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  DigitalGroundTruthGroup, DigitalSectionScore,
} from '../../redux/types';
import Prompt from '../layout/Prompt';

type Props = {
  surveyId: number;
  selectedSectionId?: number;
  groundTruthGroups: DigitalGroundTruthGroup[];
  selectedGroundTruthGroup: DigitalGroundTruthGroup | null;
  digitalSectionScores?: DigitalSectionScore[];
  addGroundTruthGroup: (gtGroup: Omit<DigitalGroundTruthGroup, 'id'>) => void;
  setSelectedGroundTruthGroup: (selectedGroup: number | null) => void;
  deleteDigitalGroundTruthGroup: (gtGroupId: number) => void;
};

const GroundTruthToolbar: React.FC<Props> = ({
  surveyId,
  selectedSectionId,
  groundTruthGroups,
  selectedGroundTruthGroup,
  digitalSectionScores,
  addGroundTruthGroup,
  setSelectedGroundTruthGroup,
  deleteDigitalGroundTruthGroup,
}: Props) => {
  const [promptDelete, setPromptDelete] = useState<boolean>(false);

  const savedScore = digitalSectionScores && digitalSectionScores
    .find((dss) => (
      dss.digitalGroundTruthGroupId === selectedGroundTruthGroup?.id
    && dss.digitalSectionId === selectedSectionId
    ));

  const onGroundTruthIdChange = (e: SelectChangeEvent) => {
    const selectedVal = e.target.value as string;
    setSelectedGroundTruthGroup(Number(selectedVal));
  };

  const selectedGroupIdExists = () => !!groundTruthGroups
    .find((g) => g.id === selectedGroundTruthGroup?.id);

  const addGroup = () => {
    const largestIndex = groundTruthGroups.length
      ? groundTruthGroups.sort((a, b) => b.index - a.index)[0].index : 0;
    const newGTGroup: Omit<DigitalGroundTruthGroup, 'id'> = {
      digitalSurveyId: surveyId,
      index: largestIndex + 1,
    };
    addGroundTruthGroup(newGTGroup);
  };

  const onDeleteGroup = () => {
    setPromptDelete(true);
  };

  const confirmDeleteGroup = (confirm: boolean) => {
    if (confirm && selectedGroundTruthGroup) {
      deleteDigitalGroundTruthGroup(selectedGroundTruthGroup.id);
    }
    setPromptDelete(false);
  };

  return (
    <Box display="flex" sx={{ my: 1 }}>
      <Box>
        <Tooltip title="Add Groundtruth" placement="top">
          <IconButton onClick={addGroup}>
            <AddIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete Groundtruth" placement="top">
          <IconButton onClick={onDeleteGroup} sx={{ mx: 1 }}>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </Box>
      <Box flexGrow={1}>
        <FormControl size="small" sx={{ width: 150 }}>
          <InputLabel id="groundtruth-group-label">GroundTruth ID</InputLabel>
          <Select
            labelId="groundtruth-group-label"
            id="groundtruth-id-select"
            value={selectedGroupIdExists() ? selectedGroundTruthGroup?.id.toString() || '' : ''}
            label="GroundTruth ID"
            onChange={onGroundTruthIdChange}
          >
            {
              groundTruthGroups.map((g) => (
                <MenuItem key={g.id} value={g.id}>{g.index}</MenuItem>
              ))
            }
          </Select>
        </FormControl>
      </Box>
      {
        selectedGroundTruthGroup && (
          <TextField
            sx={{ width: 150 }}
            size="small"
            value={savedScore?.maxScore || 0}
            label="Possible Max Score"
            disabled
          />
        )
      }
      <Prompt
        maxWidth="sm"
        title="Confirm Delete Groundtruth group?"
        display={promptDelete}
        action={confirmDeleteGroup}
        version={2}
      >
        {`GroundTruth group ${selectedGroundTruthGroup?.index} along with all its Groundtruths will be deleted`}
      </Prompt>
    </Box>
  );
};

export default GroundTruthToolbar;
